// (c) chaehongee. Confidential and proprietary.

import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import {
    createCheckoutSession,
    type BillingConfig,
    type CreditPreset,
} from '../../services/billing'
import { setCheckoutReturnTo } from '../../services/checkoutReturn'
import { redeemPromoCode } from '../../services/promoCodes'

import styles from './CreditPurchaseForm.module.css'

interface CreditPurchaseFormProps {
    billingConfig: BillingConfig
    onPurchaseStart?: () => void
    onError?: (message: string) => void
    onCreditsAdded?: (credits: number, balance: number) => void
}

export function CreditPurchaseForm({
    billingConfig,
    onPurchaseStart,
    onError,
    onCreditsAdded,
}: CreditPurchaseFormProps): ReactElement {
    const { t } = useTranslation('billing')
    const [selectedPreset, setSelectedPreset] = useState<CreditPreset | null>(
        billingConfig.presets[0] ?? null
    )
    const [isPurchasing, setIsPurchasing] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [promoCode, setPromoCode] = useState('')
    const [isRedeeming, setIsRedeeming] = useState(false)
    const [redeemError, setRedeemError] = useState<string | null>(null)
    const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null)

    const formatAmount = (amount: number) => {
        if (billingConfig.currency === 'krw') {
            return `${billingConfig.symbol}${new Intl.NumberFormat().format(amount)}`
        }
        return `${billingConfig.symbol}${amount}`
    }

    const handlePurchase = async () => {
        if (!selectedPreset || isPurchasing) return
        try {
            setIsPurchasing(true)
            setError(null)
            onPurchaseStart?.()
            const res = await createCheckoutSession({
                currency: billingConfig.currency,
                amount: selectedPreset.amount,
            })
            setCheckoutReturnTo()
            window.location.href = res.checkout_url
        } catch {
            const msg = t('purchaseFailed')
            setError(msg)
            onError?.(msg)
        } finally {
            setIsPurchasing(false)
        }
    }

    const handleRedeem = async () => {
        const code = promoCode.trim()
        if (!code || isRedeeming) return
        try {
            setIsRedeeming(true)
            setRedeemError(null)
            setRedeemSuccess(null)
            const response = await redeemPromoCode(code)
            setRedeemSuccess(
                t('redeemSuccess', {
                    code: response.code,
                    credits: new Intl.NumberFormat().format(response.credits_added),
                })
            )
            setPromoCode('')
            onCreditsAdded?.(response.credits_added, response.credit_balance)
        } catch {
            setRedeemError(t('redeemFailed'))
        } finally {
            setIsRedeeming(false)
        }
    }

    return (
        <div className={styles.form}>
            <div className={styles.presetGrid}>
                {billingConfig.presets.map((preset) => (
                    <button
                        key={preset.amount}
                        type="button"
                        className={styles.presetButton}
                        data-selected={selectedPreset?.amount === preset.amount}
                        onClick={() => setSelectedPreset(preset)}
                    >
                        <span className={styles.presetAmount}>
                            {formatAmount(preset.amount)}
                        </span>
                        <span className={styles.presetCredits}>
                            {new Intl.NumberFormat().format(preset.credits)} {t('common:credits')}
                        </span>
                    </button>
                ))}
            </div>

            <button
                type="button"
                className={styles.primaryButton}
                onClick={handlePurchase}
                disabled={!selectedPreset || isPurchasing}
            >
                {isPurchasing ? t('redirecting') : t('buyCredits')}
            </button>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.divider} />

            <div className={styles.promoSection}>
                <div className={styles.promoTitle}>{t('promoCode')}</div>
                <div className={styles.promoRow}>
                    <input
                        className={styles.promoInput}
                        type="text"
                        placeholder={t('promoCode')}
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        aria-label={t('promoCode')}
                    />
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={handleRedeem}
                        disabled={promoCode.trim().length === 0 || isRedeeming}
                    >
                        {isRedeeming ? t('redeeming') : t('redeem')}
                    </button>
                </div>
                {redeemSuccess && (
                    <div className={styles.success}>{redeemSuccess}</div>
                )}
                {redeemError && (
                    <div className={styles.error}>{redeemError}</div>
                )}
            </div>
        </div>
    )
}
